import React, { useEffect, useState } from "react";
import { apiFetchVeyaTeam } from "../../lib/api";


function resolveImageUrl(value) {
  if (!value) return "";

  const url =
    typeof value === "string"
      ? value
      : value.url || value.image_url || value.src || value.original_url || "";

  if (!url) return "";

  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/tryon-assets/")) return `${process.env.REACT_APP_API_BASE_URL || "http://localhost:8000"}${url}`;

  return url;
}

function getProductImages(product) {
  const images = [];

  if (product.image_url) images.push(product.image_url);

  if (Array.isArray(product.images)) {
    for (const item of product.images) {
      const url = resolveImageUrl(item);
      if (url) images.push(url);
    }
  }

  return [...new Set(images)];
}

export default function VeyaTeamMerchants() {
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
const [editForm, setEditForm] = useState(null);
const [filter, setFilter] = useState("all");

const [applications, setApplications] = useState([]);
const [applicationsLoading, setApplicationsLoading] = useState(true);
const [selectedApplicationId, setSelectedApplicationId] = useState(null);
const [applicationNotes, setApplicationNotes] = useState({});
const [creatingAccountId, setCreatingAccountId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

async function loadApplications() {
  setApplicationsLoading(true);
  setError("");

  try {
    const data = await apiFetchVeyaTeam("/veya-team/merchants/applications");
    setApplications(Array.isArray(data) ? data : []);
  } catch (err) {
    console.error("Failed to load merchant applications", err);
    setError(err?.message || "Could not load merchant applications.");
  } finally {
    setApplicationsLoading(false);
  }
}

async function updateApplicationStatus(application, status) {
  setError("");

  try {
    const updated = await apiFetchVeyaTeam(
      `/veya-team/merchants/applications/${application.id}`,
      {
        method: "PATCH",
        body: JSON.stringify({
          status,
          internal_notes: applicationNotes[application.id] ?? application.internal_notes ?? "",
        }),
      }
    );

    setApplications((prev) =>
      prev.map((item) => (item.id === updated.id ? updated : item))
    );
  } catch (err) {
    console.error("Failed to update application", err);
    setError(err?.message || "Could not update application.");
  }
}

async function createAccountFromApplication(application) {
  const confirmed = window.confirm(
    `Create a merchant account for ${application.store_name}? This should only happen after the contract is signed.`
  );

  if (!confirmed) return;

  setCreatingAccountId(application.id);
  setError("");

  try {
    const result = await apiFetchVeyaTeam(
      `/veya-team/merchants/applications/${application.id}/create-account`,
      {
        method: "POST",
      }
    );

    await loadApplications();
    await loadMerchants();

    alert(result?.message || "Merchant account created.");
  } catch (err) {
    console.error("Failed to create merchant account", err);
    setError(err?.message || "Could not create merchant account.");
  } finally {
    setCreatingAccountId(null);
  }
}

function updateApplicationNote(applicationId, value) {
  setApplicationNotes((prev) => ({
    ...prev,
    [applicationId]: value,
  }));
}

  async function loadMerchants() {
    setLoading(true);
    setError("");

    try {
      const data = await apiFetchVeyaTeam("/veya-team/merchants/active");
      setMerchants(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load merchants", err);
      setError(err?.message || "Could not load merchants.");
    } finally {
      setLoading(false);
    }
  }

  async function openMerchant(merchant) {
    setSelectedMerchant(merchant);
    setEditingProduct(null);
    setEditForm(null);
    setCatalogLoading(true);
    setError("");

    try {
      const data = await apiFetchVeyaTeam(`/veya-team/merchants/${merchant.id}/catalog`);
      setCatalog(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load catalog", err);
      setError(err?.message || "Could not load catalog.");
    } finally {
      setCatalogLoading(false);
    }
  }

  function startEditing(product) {
    setEditingProduct(product);
    setEditForm({
      title: product.title || "",
      description: product.description || "",
      brand: product.brand || "",
      category: product.category || "",
      color: product.color || "",
      material: product.material || "",
      price_amount: product.price_amount || "",
      currency: product.currency || "NZD",
      image_url: product.image_url || "",
      images: Array.isArray(product.images) ? product.images.join("\n") : "",
      is_active: Boolean(product.is_active),
    });
  }

  function updateEditField(key, value) {
    setEditForm((prev) => ({ ...prev, [key]: value }));
  }

  async function quickUpdateProduct(productId, patch) {
  setError("");

  try {
    const updated = await apiFetchVeyaTeam(
      `/veya-team/merchants/products/${productId}`,
      {
        method: "PATCH",
        body: JSON.stringify(patch),
      }
    );

    setCatalog((prev) =>
      prev.map((product) => (product.id === updated.id ? updated : product))
    );
  } catch (err) {
    console.error("Failed to quick update product", err);
    setError(err?.message || "Could not update product.");
  }
}

  async function saveProductEdits(e) {
    e.preventDefault();

    if (!editingProduct || !editForm) return;

    setSaving(true);
    setError("");

    try {
      const updated = await apiFetchVeyaTeam(
        `/veya-team/merchants/products/${editingProduct.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            ...editForm,
            images: String(editForm.images || "")
              .split("\n")
              .map((line) => line.trim())
              .filter(Boolean),
          }),
        }
      );

      setCatalog((prev) =>
        prev.map((product) => (product.id === updated.id ? updated : product))
      );

      setEditingProduct(null);
      setEditForm(null);
    } catch (err) {
      console.error("Failed to update product", err);
      setError(err?.message || "Could not update product.");
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
  loadApplications();
  loadMerchants();
}, []);

 const filteredCatalog = catalog.filter((product) => {
  const hasCatalogIssue =
    !product.title ||
    !product.category ||
    !product.image_url ||
    !product.price_amount;

  if (filter === "issues") return hasCatalogIssue;
  if (filter === "inactive") return !product.is_active;

  return true;
});

const activeApplications = applications.filter(
  (app) => app.status !== "account_created" && app.status !== "declined"
);

const completedApplications = applications.filter(
  (app) => app.status === "account_created" || app.status === "declined"
);

return (
  <div style={{ display: "grid", gap: 18 }}>
    {/* MERCHANT APPLICATIONS REVIEW */}
    <div className="merchant-card" style={{ padding: 22 }}>
      <h2 style={{ marginTop: 0, lineHeight: 1.15 }}>
        Merchant Applications
      </h2>

      <p style={{ marginTop: 6, opacity: 0.72 }}>
        Review boutique applications, track contract status, and create merchant
        accounts only after contracts are signed.
      </p>

      {applicationsLoading ? <p>Loading applications...</p> : null}

{!applicationsLoading && applications.length === 0 ? (
  <p style={{ opacity: 0.72 }}>No merchant applications yet.</p>
) : null}

{!applicationsLoading && activeApplications.length > 0 ? (
  <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
    {activeApplications.map((application) => {
      const isOpen = selectedApplicationId === application.id;

      return (
        <div
          key={application.id}
          style={{
            padding: 14,
            borderRadius: 18,
            border: "1px solid rgba(176,140,108,0.24)",
            background: "rgba(251,247,242,0.58)",
          }}
        >
          <button
            type="button"
            onClick={() =>
              setSelectedApplicationId(isOpen ? null : application.id)
            }
            style={{
              width: "100%",
              textAlign: "left",
              border: "none",
              background: "transparent",
              padding: 0,
              cursor: "pointer",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 14,
                alignItems: "flex-start",
              }}
            >
              <div>
                <strong>{application.store_name}</strong>

                <div style={{ fontSize: 13, opacity: 0.72, marginTop: 4 }}>
                  {application.email} · {application.location || "No location"}
                </div>

                <div style={{ fontSize: 13, opacity: 0.72, marginTop: 3 }}>
                  {application.retailer_type || "No retailer type"} ·{" "}
                  {application.product_count || "No product count"}
                </div>
              </div>

              <span className="pill pill--level">{application.status}</span>
            </div>
          </button>

          {isOpen ? (
            <div style={{ marginTop: 16, display: "grid", gap: 14 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12,
                }}
              >
                <div>
                  <div className="sectiontitle">Website</div>
                  <a
                    href={application.website}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "var(--burgundy)" }}
                  >
                    {application.website}
                  </a>
                </div>

                <div>
                  <div className="sectiontitle">Instagram</div>
                  <div>{application.instagram || "—"}</div>
                </div>

                <div>
                  <div className="sectiontitle">Contact</div>
                  <div>{application.contact_name}</div>
                </div>

                <div>
                  <div className="sectiontitle">Platform</div>
                  <div>{application.ecommerce_platform || "—"}</div>
                </div>

                <div>
                  <div className="sectiontitle">Front-facing images</div>
                  <div>{application.has_front_facing_images || "—"}</div>
                </div>

                <div>
                  <div className="sectiontitle">Early feedback</div>
                  <div>{application.open_to_early_feedback || "—"}</div>
                </div>
              </div>

              <div>
                <div className="sectiontitle">Product categories</div>
                <p style={{ margin: 0, lineHeight: 1.55 }}>
                  {application.product_categories || "—"}
                </p>
              </div>

              <div>
                <div className="sectiontitle">Store point of view</div>
                <p style={{ margin: 0, lineHeight: 1.55 }}>
                  {application.store_point_of_view || "—"}
                </p>
              </div>

              <label className="merchant-field">
                <span
                  className="merchant-label"
                  style={{ color: "rgba(43,43,43,0.62)" }}
                >
                  Internal notes
                </span>

                <textarea
                  className="merchant-input merchant-textarea merchant-textarea--compact"
                  value={
                    applicationNotes[application.id] ??
                    application.internal_notes ??
                    ""
                  }
                  onChange={(e) =>
                    updateApplicationNote(application.id, e.target.value)
                  }
                  placeholder="Notes on fit, concerns, contract status, follow-up..."
                />
              </label>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() =>
                    updateApplicationStatus(application, "needs_more_info")
                  }
                >
                  Needs more info
                </button>

                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() =>
                    updateApplicationStatus(application, "waitlisted")
                  }
                >
                  Waitlist
                </button>

                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() =>
                    updateApplicationStatus(application, "declined")
                  }
                >
                  Decline
                </button>

                <button
                  type="button"
                  className="gold-btn"
                  onClick={() =>
                    updateApplicationStatus(application, "approved")
                  }
                >
                  Approve
                </button>

                <button
                  type="button"
                  className="gold-btn"
                  onClick={() =>
                    updateApplicationStatus(application, "contract_sent")
                  }
                >
                  Mark contract sent
                </button>

                <button
                  type="button"
                  className="gold-btn"
                  onClick={() =>
                    updateApplicationStatus(application, "contract_signed")
                  }
                >
                  Mark contract signed
                </button>

                {application.status === "contract_signed" ? (
                  <button
                    type="button"
                    className="gold-btn"
                    disabled={creatingAccountId === application.id}
                    onClick={() => createAccountFromApplication(application)}
                  >
                    {creatingAccountId === application.id
                      ? "Creating..."
                      : "Create merchant account"}
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      );
    })}
  </div>
) : null}

{!applicationsLoading && completedApplications.length > 0 ? (
  <details style={{ marginTop: 18 }}>
    <summary style={{ cursor: "pointer", fontWeight: 700 }}>
      Completed / declined applications
    </summary>

    <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
      {completedApplications.map((application) => (
        <div
          key={application.id}
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            padding: 10,
            borderRadius: 14,
            background: "rgba(255,255,255,0.42)",
            border: "1px solid rgba(176,140,108,0.18)",
          }}
        >
          <div>
            <strong>{application.store_name}</strong>
            <div style={{ fontSize: 12, opacity: 0.68 }}>
              {application.email}
            </div>
          </div>

          <span className="pill pill--level">{application.status}</span>
        </div>
      ))}
    </div>
  </details>
) : null} 
    </div>

    <div className="merchant-card" style={{ padding: 22 }}>
      <h2 style={{ marginTop: 0, lineHeight: 1.15 }}>Merchants</h2>
        <p style={{ marginTop: 6, opacity: 0.72 }}>
          View active merchants, open their catalog, and tidy product details.
        </p>

        {loading ? <p>Loading merchants...</p> : null}
        {error ? <p className="merchant-error">{error}</p> : null}

        {!loading && merchants.length === 0 ? (
          <p style={{ opacity: 0.72 }}>No active merchants found.</p>
        ) : null}

        <div style={{ display: "grid", gap: 10, marginTop: 16 }}>
          {merchants.map((merchant) => (
            <button
              key={merchant.id}
              type="button"
              onClick={() => openMerchant(merchant)}
              style={{
                textAlign: "left",
                padding: 14,
                borderRadius: 16,
                border:
                  selectedMerchant?.id === merchant.id
                    ? "1px solid rgba(91,31,43,0.34)"
                    : "1px solid rgba(176,140,108,0.22)",
                background:
                  selectedMerchant?.id === merchant.id
                    ? "rgba(91,31,43,0.08)"
                    : "rgba(251,247,242,0.55)",
                cursor: "pointer",
              }}
            >
              <strong>{merchant.name}</strong>
              <div style={{ fontSize: 13, opacity: 0.7, marginTop: 3 }}>
                {merchant.contact_email}
              </div>
              <div style={{ fontSize: 12, opacity: 0.58, marginTop: 3 }}>
                Status: {merchant.status} · /{merchant.slug}
              </div>
            </button>
          ))}
        </div>
      </div>

      {selectedMerchant ? (
        <div className="merchant-card" style={{ padding: 22 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 14,
              alignItems: "flex-start",
              marginBottom: 14,
            }}
          >
            <div>
              <h2 style={{ margin: 0, lineHeight: 1.15 }}>
                {selectedMerchant.name} catalog
              </h2>
              <p style={{ margin: "6px 0 0", opacity: 0.72 }}>
                {catalog.length} products
              </p>
            </div>
          </div>

          {catalogLoading ? <p>Loading catalog...</p> : null}

{!catalogLoading && catalog.length === 0 ? (
  <p style={{ opacity: 0.72 }}>No products found for this merchant.</p>
) : null}

{!catalogLoading && catalog.length > 0 ? (
  <div
    style={{
      display: "flex",
      gap: 8,
      flexWrap: "wrap",
      marginBottom: 14,
    }}
  >
    <button
      type="button"
      onClick={() => setFilter("all")}
      className={filter === "all" ? "gold-btn" : ""}
      style={{
        padding: "8px 12px",
        borderRadius: 999,
        border: "1px solid rgba(176,140,108,0.28)",
        background: filter === "all" ? undefined : "rgba(251,247,242,0.65)",
        cursor: "pointer",
      }}
    >
      All
    </button>

    <button
      type="button"
      onClick={() => setFilter("issues")}
      className={filter === "issues" ? "gold-btn" : ""}
      style={{
        padding: "8px 12px",
        borderRadius: 999,
        border: "1px solid rgba(176,140,108,0.28)",
        background: filter === "issues" ? undefined : "rgba(251,247,242,0.65)",
        cursor: "pointer",
      }}
    >
      Needs tidy
    </button>

    <button
      type="button"
      onClick={() => setFilter("inactive")}
      className={filter === "inactive" ? "gold-btn" : ""}
      style={{
        padding: "8px 12px",
        borderRadius: 999,
        border: "1px solid rgba(176,140,108,0.28)",
        background: filter === "inactive" ? undefined : "rgba(251,247,242,0.65)",
        cursor: "pointer",
      }}
    >
      Inactive
    </button>
  </div>
) : null}

<div style={{ display: "grid", gap: 12 }}>
            {filteredCatalog.map((product) => {
              const images = getProductImages(product);
              const hasCatalogIssue =
                !product.title ||
                !product.category ||
                !product.image_url ||
                !product.price_amount;
  

              return (
                <div
                  key={product.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "82px minmax(0, 1fr) auto",
                    gap: 12,
                    alignItems: "center",
                    padding: 12,
                    borderRadius: 18,
                    border: hasCatalogIssue
                      ? "1px solid rgba(122,90,43,0.34)"
                      : "1px solid rgba(176,140,108,0.22)",
                    background: hasCatalogIssue
                      ? "rgba(122,90,43,0.06)"
                      : "rgba(251,247,242,0.55)",
                  }}
                >
                  <div
                    style={{
                      width: 82,
                      height: 104,
                      borderRadius: 14,
                      overflow: "hidden",
                      background: "rgba(0,0,0,0.05)",
                      border: "1px solid rgba(176,140,108,0.22)",
                    }}
                  >
                    {images[0] ? (
                      <img
                        src={resolveImageUrl(images[0])}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    ) : null}
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <strong style={{ lineHeight: 1.25 }}>
                      {product.title || "Untitled product"}
                    </strong>

                    <div style={{ fontSize: 13, opacity: 0.72, marginTop: 5 }}>
                      {product.brand || "No brand"} · {product.category || "Missing category"}
                    </div>

                    <div style={{ fontSize: 13, opacity: 0.72, marginTop: 3 }}>
                      {product.color || "No colour"} · {product.material || "No material"}
                    </div>

                    <div style={{ fontSize: 13, opacity: 0.72, marginTop: 3 }}>
                      {product.currency || "NZD"} {product.price_amount || "Missing price"} ·{" "}
                      {product.is_active ? "Active" : "Inactive"}
                    </div>

                    {hasCatalogIssue ? (
                      <div
                        style={{
                          display: "inline-flex",
                          marginTop: 8,
                          padding: "4px 8px",
                          borderRadius: 999,
                          background: "rgba(122,90,43,0.10)",
                          border: "1px solid rgba(122,90,43,0.16)",
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#7a5a2b",
                        }}
                      >
                        Needs tidy
                      </div>
                    ) : null}
                  </div>

<div
  style={{
    display: "flex",
    flexDirection: "column",
    gap: 8,
    alignItems: "flex-end",
  }}
>
  {!product.is_active ? (
    <button
      type="button"
      onClick={() => quickUpdateProduct(product.id, { is_active: true })}
      style={{
        fontSize: 12,
        padding: "6px 10px",
        borderRadius: 10,
        border: "1px solid rgba(47,107,63,0.18)",
        background: "rgba(47,107,63,0.08)",
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      Activate
    </button>
  ) : null}

  {product.is_active ? (
    <button
      type="button"
      onClick={() => quickUpdateProduct(product.id, { is_active: false })}
      style={{
        fontSize: 12,
        padding: "6px 10px",
        borderRadius: 10,
        border: "1px solid rgba(122,90,43,0.18)",
        background: "rgba(122,90,43,0.08)",
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      Hide
    </button>
  ) : null}

  <button
    type="button"
    className="gold-btn"
    onClick={() => startEditing(product)}
    style={{
      whiteSpace: "nowrap",
      minHeight: 38,
      padding: "8px 14px",
    }}
  >
    Tidy
  </button>
</div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {editingProduct && editForm ? (
        <div
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !saving) {
              setEditingProduct(null);
              setEditForm(null);
            }
          }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            display: "grid",
            placeItems: "center",
            padding: 18,
            background: "rgba(35,24,24,0.28)",
            backdropFilter: "blur(7px)",
          }}
        >
          <div
            className="merchant-card"
            role="dialog"
            aria-modal="true"
            aria-label="Tidy product"
            style={{
              width: "min(760px, calc(100vw - 36px))",
              maxHeight: "calc(100vh - 44px)",
              overflow: "auto",
              padding: 24,
              boxShadow: "0 28px 80px rgba(35,24,24,0.22)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 14,
                alignItems: "flex-start",
                marginBottom: 16,
              }}
            >
              <div>
                <h2 style={{ margin: 0, lineHeight: 1.15 }}>Tidy product</h2>
                <p style={{ margin: "6px 0 0", opacity: 0.72 }}>
                  Editing catalog details for {selectedMerchant?.name}.
                </p>
              </div>

              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  setEditingProduct(null);
                  setEditForm(null);
                }}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  border: "1px solid rgba(0,0,0,0.10)",
                  background: "rgba(255,255,255,0.74)",
                  cursor: saving ? "not-allowed" : "pointer",
                  fontSize: 20,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            <form
              className="merchant-form"
              onSubmit={saveProductEdits}
              style={{ display: "grid", gap: 12 }}
            >
              <input
                className="merchant-input"
                value={editForm.title}
                onChange={(e) => updateEditField("title", e.target.value)}
                placeholder="Product title"
              />

              <textarea
                className="merchant-input"
                rows={3}
                value={editForm.description}
                onChange={(e) => updateEditField("description", e.target.value)}
                placeholder="Description"
              />

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <input
                  className="merchant-input"
                  value={editForm.brand}
                  onChange={(e) => updateEditField("brand", e.target.value)}
                  placeholder="Brand"
                />

                <input
                  className="merchant-input"
                  value={editForm.category}
                  onChange={(e) => updateEditField("category", e.target.value)}
                  placeholder="Category"
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <input
                  className="merchant-input"
                  value={editForm.color}
                  onChange={(e) => updateEditField("color", e.target.value)}
                  placeholder="Colour"
                />

                <input
                  className="merchant-input"
                  value={editForm.material}
                  onChange={(e) => updateEditField("material", e.target.value)}
                  placeholder="Material"
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <input
                  className="merchant-input"
                  value={editForm.price_amount}
                  onChange={(e) => updateEditField("price_amount", e.target.value)}
                  placeholder="Price"
                />

                <input
                  className="merchant-input"
                  value={editForm.currency}
                  onChange={(e) => updateEditField("currency", e.target.value)}
                  placeholder="Currency"
                />
              </div>

              <input
                className="merchant-input"
                value={editForm.image_url}
                onChange={(e) => updateEditField("image_url", e.target.value)}
                placeholder="Primary image URL"
              />

              <textarea
                className="merchant-input"
                rows={4}
                value={editForm.images}
                onChange={(e) => updateEditField("images", e.target.value)}
                placeholder="Gallery images, one per line"
              />

              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={editForm.is_active}
                  onChange={(e) => updateEditField("is_active", e.target.checked)}
                />
                <span className="merchant-label">Product active</span>
              </label>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  paddingTop: 6,
                }}
              >
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => {
                    setEditingProduct(null);
                    setEditForm(null);
                  }}
                  style={{
                    padding: "10px 16px",
                    borderRadius: 14,
                    border: "1px solid rgba(0,0,0,0.10)",
                    background: "rgba(255,255,255,0.72)",
                    cursor: saving ? "not-allowed" : "pointer",
                    fontWeight: 700,
                  }}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="gold-btn"
                  disabled={saving}
                  style={{ minHeight: 44, padding: "10px 18px" }}
                >
                  {saving ? "Saving..." : "Save changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}